import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { checkRateLimit, sanitizeInput } from '../../utils/security'
import { Surface, TextField, Label, InputGroup, TextArea, SearchField, Button, Disclosure, Alert } from '@heroui/react'
import ArrowLeft from '~icons/solar/arrow-left-outline'
import HelpCircle from '~icons/solar/question-circle-bold'
import Send from '~icons/solar/letter-bold'
import Book from '~icons/solar/book-bold'
import MessageCircle from '~icons/solar/chat-round-dots-bold'
import Mail from '~icons/solar/letter-bold'
import SettingsIcon from '~icons/solar/settings-bold'
import LogOut from '~icons/solar/logout-2-bold'
import TrendingUp from '~icons/solar/graph-up-bold'
import ExternalLink from '~icons/solar/square-top-down-bold'
import Video from '~icons/solar/videocamera-record-bold'
import DollarSign from '~icons/solar/dollar-minimalistic-bold'
import CreditCard from '~icons/solar/card-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

interface FAQ {
  id: string
  question: string
  answer: string
  category: 'getting-started' | 'commissions' | 'payments' | 'technical'
}

const faqs: FAQ[] = [
  {
    id: '1',
    category: 'getting-started',
    question: 'How do I get started as an affiliate?',
    answer: 'Getting started is easy! After signing up, you\'ll receive your unique affiliate link. Share this link with your audience through social media, blog posts, emails, or any other channel. When someone clicks your link and makes a purchase, you earn a commission!'
  },
  {
    id: '2',
    category: 'getting-started',
    question: 'Where can I find my affiliate link?',
    answer: 'Your affiliate link is displayed prominently on your dashboard. Simply click the "Copy" button to copy it to your clipboard. You can share this link anywhere you\'d like!'
  },
  {
    id: '3',
    category: 'commissions',
    question: 'How much commission do I earn?',
    answer: 'You earn 30% commission on all purchases made through your affiliate link. This applies to both one-time purchases and recurring subscriptions. For subscriptions, you\'ll continue earning commission for as long as the customer remains subscribed!'
  },
  {
    id: '4',
    category: 'commissions',
    question: 'Do I earn commission on signups?',
    answer: 'No, signups alone don\'t earn commission. You only earn commission when a referred user makes an actual purchase or subscribes to a paid plan. However, you can still track signups in your dashboard to monitor your conversion funnel.'
  },
  {
    id: '5',
    category: 'commissions',
    question: 'How long does the cookie last?',
    answer: 'Our affiliate cookie lasts for 30 days. This means if someone clicks your link and makes a purchase within 30 days, you\'ll earn the commission even if they don\'t purchase immediately.'
  },
  {
    id: '6',
    category: 'payments',
    question: 'When can I withdraw my earnings?',
    answer: 'You can request a withdrawal once your available balance reaches $50 or more. Withdrawals are typically processed within 3-5 business days.'
  },
  {
    id: '7',
    category: 'payments',
    question: 'What payment methods are supported?',
    answer: 'We support Bank Transfer (ACH), PayPal, and Stripe payouts. You can add and manage your payment methods in the Payments section.'
  },
  {
    id: '8',
    category: 'payments',
    question: 'Why is some of my earnings marked as "pending"?',
    answer: 'Earnings are held in pending status for 30 days to account for potential refunds or chargebacks. After 30 days, the earnings automatically move to your available balance and can be withdrawn.'
  },
  {
    id: '9',
    category: 'technical',
    question: 'Can I track multiple campaigns?',
    answer: 'Yes! While you have one main affiliate link, you can add custom UTM parameters to track different campaigns. For example: yourlink.com?ref=CODE&utm_source=twitter'
  },
  {
    id: '10',
    category: 'technical',
    question: 'How do I reset my password?',
    answer: 'Click on "Forgot password?" on the login page. You\'ll receive an email with a password reset link. You can also change your password anytime from the Settings page.'
  }
]

export default function HelpPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [contactError, setContactError] = useState('')

  const categories = [
    { id: 'all', label: 'All Topics', icon: Book },
    { id: 'getting-started', label: 'Getting Started', icon: TrendingUp },
    { id: 'commissions', label: 'Commissions', icon: DollarSign },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'technical', label: 'Technical', icon: SettingsIcon }
  ]

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setContactError('')

    // SECURITY: Rate limiting to prevent spam
    const rateLimit = checkRateLimit(
      `contact:${user?.email}`,
      3, // max 3 submissions
      60 * 60 * 1000, // within 1 hour
      60 * 60 * 1000  // 1 hour lockout
    )

    if (!rateLimit.allowed) {
      setContactError(rateLimit.message || 'Too many attempts. Please try again later.')
      return
    }

    setSubmitting(true)

    // SECURITY: Sanitize user inputs
    const sanitizedForm = {
      subject: sanitizeInput(contactForm.subject),
      message: sanitizeInput(contactForm.message),
      userEmail: user?.email
    }

    // TODO: Replace with actual API call
    // await fetch('/api/support/contact', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(sanitizedForm)
    // })

    await new Promise(resolve => setTimeout(resolve, 1500))

    setSubmitting(false)
    setSubmitted(true)
    setContactForm({ subject: '', message: '' })

    setTimeout(() => setSubmitted(false), 5000)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-black/10 dark:border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/affiliate/dashboard')}
                className="flex items-center gap-2 text-muted hover:text-accent transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </button>
              <div className="hidden sm:flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
                  <HelpCircle className="h-6 w-6 text-accent-foreground" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">Help Center</h1>
                  <p className="text-sm text-muted">Get answers to your questions</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/affiliate/settings')}
                className="p-2 text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors"
              >
                <SettingsIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => { logout(); navigate('/auth') }}
                className="p-2 text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 sm:py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">How can we help you?</h2>
          <div className="max-w-2xl mx-auto">
            <SearchField aria-label="Search for help" value={searchQuery} onChange={setSearchQuery}>
              <SearchField.Group className="h-14 text-lg">
                <SearchField.SearchIcon />
                <SearchField.Input className="w-full" placeholder="Search for help..." />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <a
            href="https://docs.example.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Surface variant="default" className="flex items-start gap-4 p-6 rounded-xl hover:border-accent/30 transition-all group">
              <div className="p-3 bg-surface-secondary rounded-lg">
                <Book className="h-6 w-6 text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                    Documentation
                  </h3>
                  <ExternalLink className="h-4 w-4 text-muted" />
                </div>
                <p className="text-sm text-muted">Complete guides and API reference</p>
              </div>
            </Surface>
          </a>

          <a
            href="https://youtube.com/@example"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Surface variant="default" className="flex items-start gap-4 p-6 rounded-xl hover:border-accent/30 transition-all group">
              <div className="p-3 bg-surface-secondary rounded-lg">
                <Video className="h-6 w-6 text-danger" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                    Video Tutorials
                  </h3>
                  <ExternalLink className="h-4 w-4 text-muted" />
                </div>
                <p className="text-sm text-muted">Watch step-by-step guides</p>
              </div>
            </Surface>
          </a>

          <button
            onClick={() => window.location.href = 'mailto:support@example.com'}
            className="text-left"
          >
            <Surface variant="default" className="flex items-start gap-4 p-6 rounded-xl hover:border-accent/30 transition-all group">
              <div className="p-3 bg-surface-secondary rounded-lg">
                <Mail className="h-6 w-6 text-success" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors mb-2">
                  Email Support
                </h3>
                <p className="text-sm text-muted">support@example.com</p>
              </div>
            </Surface>
          </button>
        </div>

        {/* FAQ Section */}
        <Surface variant="default" className="rounded-xl mb-8 overflow-hidden">
          <div className="p-6 border-b border-black/10 dark:border-white/10">
            <h3 className="text-lg font-semibold text-foreground">Frequently Asked Questions</h3>
          </div>

          {/* Category Filters */}
          <div className="p-6 border-b border-black/10 dark:border-white/10">
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                    activeCategory === category.id
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-surface-secondary text-muted hover:text-foreground'
                  }`}
                >
                  <category.icon className="h-4 w-4" />
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="divide-y divide-black/10 dark:divide-white/10">
            {filteredFAQs.length === 0 ? (
              <div className="p-12 text-center text-muted">
                No questions found matching your search
              </div>
            ) : (
              filteredFAQs.map(faq => (
                <Disclosure
                  key={faq.id}
                  className="p-6"
                  isExpanded={expandedFAQ === faq.id}
                  onExpandedChange={(isExpanded) => setExpandedFAQ(isExpanded ? faq.id : null)}
                >
                  <Disclosure.Heading>
                    <Button
                      slot="trigger"
                      variant="ghost"
                      className="w-full justify-between p-0 font-medium text-foreground hover:bg-transparent text-left"
                    >
                      <span className="pr-4">{faq.question}</span>
                      <Disclosure.Indicator className="text-muted shrink-0" />
                    </Button>
                  </Disclosure.Heading>
                  <Disclosure.Content>
                    <Disclosure.Body className="mt-4 text-muted leading-relaxed">
                      {faq.answer}
                    </Disclosure.Body>
                  </Disclosure.Content>
                </Disclosure>
              ))
            )}
          </div>
        </Surface>

        {/* Contact Form */}
        <Surface variant="default" className="rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="h-6 w-6 text-accent" />
            <h3 className="text-lg font-semibold text-foreground">Still need help?</h3>
          </div>

          {submitted ? (
            <Surface variant="secondary" className="p-6 rounded-lg text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-success-soft rounded-full mb-3">
                <Send className="h-6 w-6 text-success-soft-foreground" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Message Sent!</h4>
              <p className="text-sm text-muted">
                We'll get back to you within 24 hours at {user?.email}
              </p>
            </Surface>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              {contactError && (
                <Alert status="danger" className="bg-danger-soft">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description className="text-danger-soft-foreground">{contactError}</Alert.Description>
                  </Alert.Content>
                </Alert>
              )}

              <TextField
                isRequired
                name="subject"
                value={contactForm.subject}
                onChange={(v) => setContactForm({ ...contactForm, subject: v })}
              >
                <Label>Subject</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Input placeholder="What do you need help with?" />
                </InputGroup>
              </TextField>

              <TextField
                isRequired
                name="message"
                value={contactForm.message}
                onChange={(v) => setContactForm({ ...contactForm, message: v })}
              >
                <Label>Message</Label>
                <TextArea variant="secondary" placeholder="Describe your issue in detail..." rows={5} />
              </TextField>

              <Button type="submit" isDisabled={submitting}>
                {submitting ? (
                  <>
                    <LoadingSpinner className="h-4 w-4" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Message
                  </>
                )}
              </Button>

              <p className="text-xs text-muted">
                We'll respond to {user?.email} within 24 hours
              </p>
            </form>
          )}
        </Surface>
      </main>
    </div>
  )
}
